/**
 * GPS gratuito vía Geolocation API del navegador.
 * Requiere HTTPS (o localhost). Sin APIs de pago.
 */

export function isMobileBrowser() {
  if (typeof navigator === "undefined") return false;
  return /Android|webOS|iPhone|iPad|iPod|IEMobile|Opera Mini|Mobile/i.test(
    navigator.userAgent,
  );
}

export function isGeolocationSecureContext() {
  if (typeof window === "undefined") return false;
  return window.isSecureContext === true;
}

const GEO_LABELS = {
  browser: "GPS OK",
  denied: "Denegado",
  timeout: "Timeout",
  unavailable: "No disponible",
  insecure: "Sin HTTPS",
};

export function formatGeoSourceLabel(geoSource) {
  if (!geoSource) return "Sin dato";
  return GEO_LABELS[geoSource] || geoSource;
}

/**
 * @returns {Promise<{
 *   latitude?: number,
 *   longitude?: number,
 *   accuracyMeters?: number,
 *   geoSource: 'browser' | 'denied' | 'unavailable' | 'timeout' | 'insecure'
 * }>}
 */
export function getClockGeolocation({ timeoutMs } = {}) {
  const mobile = isMobileBrowser();
  const waitMs = timeoutMs ?? (mobile ? 15_000 : 20_000);

  return new Promise((resolve) => {
    if (!isGeolocationSecureContext()) {
      resolve({ geoSource: "insecure" });
      return;
    }

    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve({ geoSource: "unavailable" });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracyMeters: pos.coords.accuracy,
          geoSource: "browser",
        });
      },
      (err) => {
        if (err.code === 1) {
          resolve({ geoSource: "denied" });
          return;
        }
        if (err.code === 3) {
          resolve({ geoSource: "timeout" });
          return;
        }
        resolve({ geoSource: "unavailable" });
      },
      {
        // En escritorio, alta precisión suele fallar o tardar; Wi‑Fi/red va mejor sin forzar GPS.
        enableHighAccuracy: mobile,
        timeout: waitMs,
        maximumAge: 60_000,
      },
    );
  });
}

export function formatGeolocationHint(geo = {}) {
  switch (geo.geoSource) {
    case "browser":
      return `Ubicación registrada (±${Math.round(geo.accuracyMeters || 0)} m)`;
    case "denied":
      return "Ubicación denegada. Permite el acceso en el navegador (candado en la barra de direcciones).";
    case "timeout":
      return "No se obtuvo ubicación a tiempo. En PC, activa Ubicación en Windows y recarga.";
    case "unavailable":
      return "GPS no disponible en este navegador o dispositivo.";
    case "insecure":
      return "La geolocalización requiere HTTPS. Abre Matrix con https://…";
    default:
      return null;
  }
}

/** URL gratuita OpenStreetMap para ver el punto en historial. */
export function openStreetMapLink(latitude, longitude) {
  if (latitude == null || longitude == null) return null;
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=17/${lat}/${lng}`;
}

export function geoCellLabel(row) {
  if (row.geoSource === "browser" && row.latitude != null) {
    return `±${Math.round(row.accuracyMeters || 0)} m`;
  }
  return formatGeoSourceLabel(row.geoSource);
}
